// 26 ADM-040: the health page. A read-only view, refreshed every 30 seconds, of every number the
// rule lists. Stage 0 shows the ones that exist (the database, the game clock's queues and
// workers); every other source says plainly "Not set up yet" and the stage that brings it.
// Nothing here is made up: a number is shown only when the server sent it.
import type { SupabaseClient } from '@supabase/supabase-js';
import { useEffect, useState } from 'react';

export const HEALTH_REFRESH_MS = 30_000;

export interface QueueHealth {
  queue: string;
  waiting: number;
  running: number;
  dead: number;
  oldest_waiting_seconds: number | null;
}
export interface Health {
  checked_at: string;
  queues: QueueHealth[];
  workers: { worker_id: string; queues: string[]; seconds_since_seen: number }[];
  config: { keys: number; waiting_for_approval: number };
}

export type RowState = 'ok' | 'warn' | 'down' | 'not_set_up' | 'checking';
export interface HealthRow {
  label: string;
  value: string;
  state: RowState;
}

/** The sources of ADM-040 that are not built yet, with the stage that brings each. */
export const NOT_SET_UP: readonly { label: string; when: string }[] = [
  { label: 'Crash-free sessions', when: 'once the app has players' },
  { label: 'API error rate and answer time', when: 'with the first game commands' },
  { label: 'Weather provider', when: 'Stage 5' },
  { label: 'AI gateway', when: 'with the first AI feature' },
  { label: 'Payment provider', when: 'Stage 9' },
  { label: 'Players online, by hub', when: 'once the app has players' },
];

const WORKER_SILENT_S = 120;

export function healthRows(
  db: { state: 'checking' | 'ok' | 'down'; ms: number | null },
  h: Health | null,
): HealthRow[] {
  const rows: HealthRow[] = [
    {
      label: 'Database',
      state: db.state,
      value:
        db.state === 'checking'
          ? 'Checking…'
          : db.state === 'ok'
            ? `Working (answered in ${db.ms} ms)`
            : 'Not answering',
    },
  ];
  if (h) {
    for (const q of h.queues) {
      const value =
        `${q.waiting} waiting, ${q.running} running` +
        (q.dead ? `, ${q.dead} failed for good` : '') +
        (q.oldest_waiting_seconds != null && q.waiting
          ? ` (oldest waiting ${q.oldest_waiting_seconds} s)`
          : '');
      rows.push({
        label: `Game clock queue: ${q.queue}`,
        value,
        state: q.dead > 0 ? 'warn' : 'ok',
      });
    }
    rows.push(
      h.workers.length
        ? {
            label: 'Game clock workers',
            value: h.workers
              .map((w) => `${w.worker_id}: last seen ${w.seconds_since_seen} s ago`)
              .join('; '),
            state: h.workers.some((w) => w.seconds_since_seen > WORKER_SILENT_S) ? 'warn' : 'ok',
          }
        : {
            label: 'Game clock workers',
            value: 'None running yet (the game clock starts in Stage 4)',
            state: 'not_set_up',
          },
    );
    rows.push({
      label: 'Settings',
      value: `${h.config.keys} settings, ${h.config.waiting_for_approval} waiting for approval`,
      state: 'ok',
    });
  }
  for (const s of NOT_SET_UP)
    rows.push({ label: s.label, value: `Not set up yet (${s.when})`, state: 'not_set_up' });
  return rows;
}

export function HealthPage({ sb }: { sb: SupabaseClient }) {
  const [db, setDb] = useState<{ state: 'checking' | 'ok' | 'down'; ms: number | null }>({
    state: 'checking',
    ms: null,
  });
  const [h, setH] = useState<Health | null>(null);
  useEffect(() => {
    const load = () => {
      const t = performance.now();
      void sb
        .schema('ops')
        .rpc('admin_health')
        .then(({ data, error }) => {
          setDb({ state: error ? 'down' : 'ok', ms: Math.round(performance.now() - t) });
          setH(error ? null : (data as Health));
        });
    };
    load();
    const id = setInterval(load, HEALTH_REFRESH_MS);
    return () => clearInterval(id);
  }, [sb]);
  return (
    <section aria-labelledby="h">
      <h2 id="h">Health</h2>
      <p className="muted">This page checks again every 30 seconds.</p>
      <table>
        <tbody>
          {healthRows(db, h).map((r) => (
            <tr key={r.label}>
              <th scope="row">{r.label}</th>
              <td className={r.state === 'not_set_up' ? 'muted' : undefined}>{r.value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
